import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    SiC, SiCplusplus, SiQt, SiRust, SiApple, SiLinux, SiArm,
    SiStmicroelectronics, SiPython, SiAndroid, SiReact, SiFlutter,
    SiMariadbfoundation, SiPostgresql, SiGnu, SiCmake, SiGit,
    SiGnubash, SiDocker, SiTypescript,
} from 'react-icons/si'
import { LuBug, LuCpu, LuBox, LuCloud } from 'react-icons/lu'
import { SectionShell } from '../shared/SectionShell'
import { OrbitalMark } from '../shared/OrbitalMark'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import stackData from '../../data/stack'

/* ---------- shared data ---------- */

const GRID = stackData.gridSize       // 5
const LEAD = stackData.leadIndex      // 12 (0-based, centre of 5×5)

const TECH_ICONS = [
    SiC, SiCplusplus, SiQt, SiRust, SiApple,                     // 1-5  inner
    SiLinux, SiArm, SiStmicroelectronics,                        // 6-8  inner
    SiPython, SiAndroid, SiReact, SiFlutter,                     // 9-12 outer
    SiMariadbfoundation, SiPostgresql,                           // 13-14 outer
    SiGnu, SiCmake, LuBug, LuCpu, LuBox,                         // 15-19 outer
    SiGit, SiGnubash, SiDocker, SiTypescript, LuCloud,           // 20-24 outer
]

/* ---------- desktop 5×5 grid layout ---------- */

const CELL_SIZE = 120
const GAP = 18
const SVG_SIZE = GRID * CELL_SIZE + (GRID - 1) * GAP
const CELL_COUNT = GRID * GRID

function cellCenter(index) {
    const col = index % GRID
    const row = Math.floor(index / GRID)
    const step = CELL_SIZE + GAP
    return { x: col * step + CELL_SIZE / 2, y: row * step + CELL_SIZE / 2 }
}

function quadBezierPath(x1, y1, x2, y2) {
    const mx = (x1 + x2) / 2
    const my = (y1 + y2) / 2 - 20
    return `M ${x1.toFixed(1)} ${y1.toFixed(1)} Q ${mx.toFixed(1)} ${my.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`
}

const leadCenter = cellCenter(LEAD)

const desktopConnectors = Array.from({ length: CELL_COUNT }, (_, i) => {
    if (i === LEAD) return null
    const c = cellCenter(i)
    const dx = c.x - leadCenter.x
    const dy = c.y - leadCenter.y
    const len = Math.sqrt(dx * dx + dy * dy) * 1.3
    return { d: quadBezierPath(leadCenter.x, leadCenter.y, c.x, c.y), len }
}).filter(Boolean)

function DesktopGrid({ cellLabels }) {
    const gridCells = useMemo(() => {
        let labelIndex = 0
        return Array.from({ length: CELL_COUNT }, (_, i) => {
            if (i === LEAD) return { isLead: true, index: i }
            const label = cellLabels[labelIndex] || ''
            const Icon = TECH_ICONS[labelIndex] || LuBox
            labelIndex++
            return { isLead: false, index: i, label, Icon }
        })
    }, [cellLabels])

    return (
        <div className="stack-grid-wrap" style={{ width: SVG_SIZE }}>
            <svg
                className="stack-connectors"
                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                width={SVG_SIZE}
                height={SVG_SIZE}
                aria-hidden="true"
            >
                {desktopConnectors.map(({ d, len }, i) => (
                    <path
                        key={i}
                        d={d}
                        fill="none"
                        stroke="var(--accent-2)"
                        strokeWidth="0.6"
                        strokeOpacity="0.35"
                        strokeDasharray={len}
                        style={{ '--len': len }}
                    />
                ))}
            </svg>

            <div
                className="stack-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${GRID}, ${CELL_SIZE}px)`,
                    gap: GAP,
                }}
            >
                {gridCells.map(({ isLead, index, label, Icon }) =>
                    isLead ? (
                        <div key={index} className="stack-cell stack-cell-lead">
                            <OrbitalMark width={42} height={42} />
                            <span className="stack-cell-label stack-lead-label">PLANETARY</span>
                        </div>
                    ) : (
                        <div key={index} className="stack-cell">
                            <span className="stack-cell-icon" aria-hidden="true">
                                {Icon ? <Icon size={28} /> : null}
                            </span>
                            <span className="stack-cell-label">{label}</span>
                        </div>
                    )
                )}
            </div>
        </div>
    )
}

/* ---------- mobile concentric (orbital) layout ---------- */

const ORBIT_SIZE = 340           // square viewBox / container px
const ORBIT_R1 = 90              // inner ring radius
const ORBIT_R2 = 150             // outer ring radius
const ORBIT_INNER_COUNT = 8
const CHIP_R1 = 30               // inner chip radius (60px diameter)
const CHIP_R2 = 24               // outer chip radius (48px diameter)

function orbitPosition(i) {
    const inner = i < ORBIT_INNER_COUNT
    const ringCount = inner ? ORBIT_INNER_COUNT : 16
    const idx = inner ? i : i - ORBIT_INNER_COUNT
    /* outer ring offset by half-step so chips fall *between* inner ones */
    const offset = inner ? 0 : Math.PI / 16
    const angle = (idx / ringCount) * 2 * Math.PI - Math.PI / 2 + offset
    const r = inner ? ORBIT_R1 : ORBIT_R2
    return {
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
        inner,
    }
}

function MobileOrbit({ cellLabels }) {
    const techList = useMemo(
        () =>
            cellLabels.slice(0, 24).map((label, i) => ({
                label,
                Icon: TECH_ICONS[i] || LuBox,
                ...orbitPosition(i),
            })),
        [cellLabels]
    )

    return (
        <div className="stack-orbit" style={{ width: ORBIT_SIZE, height: ORBIT_SIZE }}>
            <svg
                className="stack-orbit-connectors"
                viewBox={`-${ORBIT_SIZE / 2} -${ORBIT_SIZE / 2} ${ORBIT_SIZE} ${ORBIT_SIZE}`}
                width={ORBIT_SIZE}
                height={ORBIT_SIZE}
                aria-hidden="true"
            >
                {/* faint orbit guide circles */}
                <circle cx="0" cy="0" r={ORBIT_R1} fill="none" stroke="var(--accent-2)" strokeWidth="0.4" strokeOpacity="0.18" strokeDasharray="2 4" />
                <circle cx="0" cy="0" r={ORBIT_R2} fill="none" stroke="var(--accent-2)" strokeWidth="0.4" strokeOpacity="0.12" strokeDasharray="2 4" />
                {/* lead → each chip ray */}
                {techList.map((t, i) => (
                    <line
                        key={i}
                        x1="0" y1="0"
                        x2={t.x} y2={t.y}
                        stroke="var(--accent-2)"
                        strokeWidth="0.5"
                        strokeOpacity="0.22"
                    />
                ))}
            </svg>

            <div className="stack-orbit-center">
                <OrbitalMark width={48} height={48} />
                <span className="stack-orbit-center-label">PLANETARY</span>
            </div>

            {techList.map(({ label, Icon, x, y, inner }, i) => {
                const chipR = inner ? CHIP_R1 : CHIP_R2
                return (
                    <div
                        key={i}
                        className={`stack-orbit-chip ${inner ? 'is-inner' : 'is-outer'}`}
                        style={{
                            width: chipR * 2,
                            height: chipR * 2,
                            left: `calc(50% + ${x}px - ${chipR}px)`,
                            top: `calc(50% + ${y}px - ${chipR}px)`,
                        }}
                        title={label}
                    >
                        <Icon size={inner ? 22 : 18} />
                    </div>
                )
            })}
        </div>
    )
}

/* ---------- main section ---------- */

export default function StackSection() {
    const { t } = useTranslation()
    const isMobile = useMediaQuery('(max-width: 768px)')

    const cellLabels = t('stack.cells', { returnObjects: true }) || []
    const labels = Array.isArray(cellLabels) ? cellLabels : []

    return (
        <SectionShell id="stack">
            <div className="section-head">
                <span className="section-num">07</span>
                <span className="section-label">{t('stack.label')}</span>
            </div>

            <div className="stack-viewport">
                {isMobile ? <MobileOrbit cellLabels={labels} /> : <DesktopGrid cellLabels={labels} />}
            </div>

            {isMobile && (
                <ul className="stack-orbit-legend" aria-label="stack labels">
                    {labels.map((label, i) => (
                        <li key={i} className="stack-orbit-legend-item">
                            <span className="stack-orbit-legend-num">{String(i + 1).padStart(2, '0')}</span>
                            <span className="stack-orbit-legend-label">{label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </SectionShell>
    )
}
